import { TriangleAlert } from 'lucide-react'
import { useMemo } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { FormControl, TextField, Typography } from 'src/modules/core/components'
import {
  LOCATION_HELPER_NOTES,
  LOCATION_LABEL,
  LOCATION_VALUE_FIELDS,
  LOCATION_VALUE_INPUT_COPY,
  LOCATION_VALUE_REQUIRED_MESSAGES,
  locationRequiresValue,
  meetUnavailableText,
} from 'src/modules/user/utils/appointment-type-form'
import { LocationSelect } from '../location-select'
import { buildLocationOptions } from './appointment-type-location-field.domain'
import type {
  AppointmentTypeLocationFieldProps,
  LocationValueInputProps,
} from './appointment-type-location-field.interface'
import classes from './appointment-type-location-field.module.scss'

const LABEL_ID = 'appointment-type-location-label'
const VALUE_INPUT_ID = 'appointment-type-location-value'
const VALUE_HINT_ID = 'appointment-type-location-value-hint'
const VALUE_ERROR_ID = 'appointment-type-location-value-error'

export function AppointmentTypeLocationField({
  control,
  calendarName,
  canHostMeet,
}: AppointmentTypeLocationFieldProps) {
  const locationType = useWatch({ control, name: 'locationType' })
  const isMeetBlocked = canHostMeet === false
  const options = useMemo(() => buildLocationOptions({ isMeetBlocked, calendarName }), [isMeetBlocked, calendarName])
  const showMeetWarning = isMeetBlocked && locationType === 'google_meet'
  // The Meet note promises a link for every booking, which this calendar cannot create.
  const helperNote = showMeetWarning ? undefined : LOCATION_HELPER_NOTES[locationType]

  return (
    <FormControl label={<span id={LABEL_ID}>{LOCATION_LABEL}</span>} className={classes.field}>
      <Controller
        name="locationType"
        control={control}
        render={({ field }) => (
          <LocationSelect labelId={LABEL_ID} value={field.value} options={options} onChange={field.onChange} />
        )}
      />
      {locationRequiresValue(locationType) && (
        <LocationValueInput key={locationType} control={control} locationType={locationType} />
      )}
      {showMeetWarning && (
        <div role="alert" className={classes.warning}>
          <TriangleAlert size={18} aria-hidden className={classes.warningIcon} />
          <Typography variant="body" element="span" className={classes.warningText}>
            {meetUnavailableText(calendarName)}
          </Typography>
        </div>
      )}
      {helperNote && (
        <Typography variant="body" className={classes.helperNote}>
          {helperNote}
        </Typography>
      )}
    </FormControl>
  )
}

function LocationValueInput({ control, locationType }: LocationValueInputProps) {
  const requiredMessage = LOCATION_VALUE_REQUIRED_MESSAGES[locationType]
  const { label, placeholder, hint } = LOCATION_VALUE_INPUT_COPY[locationType]

  return (
    <Controller
      name={LOCATION_VALUE_FIELDS[locationType]}
      control={control}
      rules={{ validate: (value) => String(value ?? '').trim() !== '' || requiredMessage }}
      render={({ field, fieldState }) => (
        <TextField
          ref={field.ref}
          id={VALUE_INPUT_ID}
          type="text"
          label={label}
          placeholder={placeholder}
          hint={hint}
          hintId={VALUE_HINT_ID}
          errorId={VALUE_ERROR_ID}
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          aria-required
          aria-invalid={Boolean(fieldState.error)}
          aria-describedby={fieldState.error ? `${VALUE_HINT_ID} ${VALUE_ERROR_ID}` : VALUE_HINT_ID}
          error={fieldState.error?.message}
          wrapperClassName={classes.valueInput}
        />
      )}
    />
  )
}
